import React, { useMemo, useState } from 'react';
import { Search, Plus, ClipboardCheck, Pencil, X, Mail, AlertTriangle, CheckCircle2, Clock, Loader2, Filter, Send, History, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useFetch, Spinner } from '../lib/useFetch';
import { sopralluoghiApi, companiesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { sopralluogoFields, SOPRALLUOGO_TIPI } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TIPO_BADGE = {
  sicurezza:    { bg: '#e0e7ff', color: '#023F80', label: 'Sicurezza' },
  haccp:        { bg: '#fef7d4', color: '#8a6608', label: 'HACCP' },
  piscine:      { bg: '#dcfce7', color: '#166534', label: 'Piscine' },
  legionella:   { bg: '#cffafe', color: '#155e75', label: 'Legionella' },
  antincendio:  { bg: '#fee2e2', color: '#b91c1c', label: 'Antincendio' },
};

const daysBetween = (iso) => {
  if (!iso) return null;
  const t = new Date(iso + 'T00:00:00');
  if (Number.isNaN(t.getTime())) return null;
  const diff = (t.getTime() - Date.now()) / 86400000;
  return Math.ceil(diff);
};

const statusBadge = (nextIso, daysBefore = 30) => {
  const d = daysBetween(nextIso);
  if (d === null) return { label: '—', cls: 'bg-slate-100 text-slate-600' };
  if (d < 0) return { label: `Scaduto da ${Math.abs(d)}gg`, cls: 'bg-red-100 text-red-800', icon: AlertTriangle };
  if (d <= (daysBefore || 30)) return { label: `Tra ${d}gg`, cls: 'bg-amber-100 text-amber-800', icon: Clock };
  return { label: `Tra ${d}gg`, cls: 'bg-emerald-50 text-emerald-800', icon: CheckCircle2 };
};

const SopralluoghiPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [statoFilter, setStatoFilter] = useState('all');
  const [checking, setChecking] = useState(false);

  const { data: items, loading, reload } = useFetch(() => sopralluoghiApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);

  const crud = useCrudActions({ api: sopralluoghiApi, label: 'sopralluogo', onChange: reload });

  // Complete-inspection modal state
  const [completing, setCompleting] = useState(null); // record being completed
  const [compForm, setCompForm] = useState({ date: '', note: '', nextDate: '' });
  const [submittingComp, setSubmittingComp] = useState(false);

  // History view modal state
  const [historyOf, setHistoryOf] = useState(null);

  const openComplete = (rec) => {
    const today = new Date().toISOString().slice(0, 10);
    setCompForm({ date: today, note: '', nextDate: rec.nextDate || '' });
    setCompleting(rec);
  };

  const submitComplete = async () => {
    if (!completing) return;
    if (!compForm.date) { toast.warning('Data esecuzione obbligatoria'); return; }
    setSubmittingComp(true);
    try {
      await sopralluoghiApi.complete(completing.id, {
        date: compForm.date,
        note: compForm.note || '',
        nextDate: compForm.nextDate || null,
      });
      toast.success('Sopralluogo registrato nello storico');
      setCompleting(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore registrazione');
    } finally { setSubmittingComp(false); }
  };

  const removeHistoryEntry = async (rid, hid) => {
    if (!window.confirm('Rimuovere questa voce dallo storico?')) return;
    try {
      const res = await sopralluoghiApi.deleteHistory(rid, hid);
      toast.success('Voce rimossa');
      setHistoryOf(res.data);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rimozione');
    }
  };

  const companyName = (id) => (companies || []).find((c) => c.id === id)?.name || '—';

  const filtered = useMemo(() => {
    let list = items || [];
    if (tipoFilter !== 'all') list = list.filter((x) => Array.isArray(x.tipi) && x.tipi.includes(tipoFilter));
    if (statoFilter !== 'all') {
      list = list.filter((x) => {
        const d = daysBetween(x.nextDate);
        if (statoFilter === 'scaduto') return d !== null && d < 0;
        if (statoFilter === 'imminente') return d !== null && d >= 0 && d <= (x.alertDaysBefore || 30);
        if (statoFilter === 'ok') return d !== null && d > (x.alertDaysBefore || 30);
        if (statoFilter === 'no_alert') return !x.alertEmail;
        return true;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((x) =>
        `${companyName(x.companyId)} ${(x.tipi || []).join(' ')} ${x.note || ''} ${x.alertEmail || ''}`.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      const da = daysBetween(a.nextDate);
      const db = daysBetween(b.nextDate);
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    });
  }, [items, search, tipoFilter, statoFilter, companies]);

  const stats = useMemo(() => {
    const list = items || [];
    let scaduti = 0; let imminenti = 0; let ok = 0; let noEmail = 0;
    list.forEach((x) => {
      const d = daysBetween(x.nextDate);
      if (!x.alertEmail) noEmail += 1;
      if (d === null) return;
      if (d < 0) scaduti += 1;
      else if (d <= (x.alertDaysBefore || 30)) imminenti += 1;
      else ok += 1;
    });
    return { totale: list.length, scaduti, imminenti, ok, noEmail };
  }, [items]);

  const checkAlertsNow = async () => {
    setChecking(true);
    try {
      const res = await sopralluoghiApi.checkAlerts();
      const { sent = 0, skipped = 0, errors = 0 } = res.data || {};
      if (errors > 0 && sent === 0) {
        toast.error(`Nessun alert inviato (${errors} errori). Verifica config Resend.`);
      } else {
        toast.success(`Alert: ${sent} inviati, ${skipped} ignorati, ${errors} errori`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore controllo alert');
    } finally { setChecking(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="sopralluoghi-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center"><ClipboardCheck size={20} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Sopralluoghi</h2>
              <p className="text-xs text-slate-500">Pianifica controlli su sicurezza, HACCP, piscine, legionella, antincendio e ricevi alert email automatici.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isClient && (
              <>
                <button
                  onClick={checkAlertsNow}
                  disabled={checking}
                  data-testid="sopralluoghi-check-alerts"
                  title="Esegui subito il controllo degli alert configurati"
                  className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {checking ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} INVIA ALERT ORA
                </button>
                <button
                  onClick={() => crud.onCreate({ tipi: ['sicurezza'], alertDaysBefore: 30 })}
                  data-testid="sopralluoghi-new-btn"
                  className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"
                >
                  <Plus size={14} /> NUOVO SOPRALLUOGO
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <Kpi label="Totali" value={stats.totale} bg="#e0e7ff" color="#023F80" />
          <Kpi label="Scaduti" value={stats.scaduti} bg="#fee2e2" color="#b91c1c" />
          <Kpi label="Imminenti" value={stats.imminenti} bg="#fef7d4" color="#8a6608" />
          <Kpi label="In regola" value={stats.ok} bg="#dcfce7" color="#166534" />
          <Kpi label="Senza alert" value={stats.noEmail} bg="#f1f5f9" color="#475569" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca azienda, tipo o email…"
              className="pl-9 h-10 border-gray-200"
              data-testid="sopralluoghi-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-400" />
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger data-testid="sopralluoghi-filter-tipo" className="w-40 h-9 border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {SOPRALLUOGO_TIPI.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger data-testid="sopralluoghi-filter-stato" className="w-40 h-9 border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="scaduto">Scaduti</SelectItem>
                <SelectItem value="imminente">Imminenti</SelectItem>
                <SelectItem value="ok">In regola</SelectItem>
                <SelectItem value="no_alert">Senza alert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Azienda</th>
                <th className="text-left bg-gray-100 px-4 py-3">Tipo</th>
                <th className="text-left bg-gray-100 px-4 py-3">Ultimo</th>
                <th className="text-left bg-gray-100 px-4 py-3">Prossimo</th>
                <th className="text-left bg-gray-100 px-4 py-3">Alert email</th>
                <th className="text-center bg-gray-100 px-4 py-3">Preavviso</th>
                <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
                <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((x) => {
                const st = statusBadge(x.nextDate, x.alertDaysBefore);
                const StIcon = st.icon;
                return (
                  <tr key={x.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors" data-testid={`sopralluoghi-row-${x.id}`}>
                    <td className="px-4 py-3 font-semibold text-gray-800">{companyName(x.companyId)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(x.tipi && x.tipi.length ? x.tipi : []).map((t) => {
                          const b = TIPO_BADGE[t] || { bg: '#f1f5f9', color: '#475569', label: t };
                          return (
                            <span key={t} className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: b.bg, color: b.color }}>
                              {b.label}
                            </span>
                          );
                        })}
                        {(!x.tipi || x.tipi.length === 0) && <span className="text-slate-400 italic text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(x.lastDate) || '—'}</td>
                    <td className="px-4 py-3 text-gray-700 font-semibold">{fmtDate(x.nextDate) || '—'}</td>
                    <td className="px-4 py-3">
                      {x.alertEmail ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700">
                          <Mail size={12} className="text-[#023F80]" /> {x.alertEmail}
                        </span>
                      ) : <span className="text-slate-400 italic text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{x.alertEmail ? `${x.alertDaysBefore ?? 30}gg` : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>
                        {StIcon && <StIcon size={11} />} {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setHistoryOf(x)}
                          title={`Storico (${(x.history || []).length})`}
                          data-testid={`sopralluoghi-history-${x.id}`}
                          className="relative p-1.5 rounded-md hover:bg-gray-100"
                        >
                          <History size={14} className="text-slate-600" />
                          {(x.history && x.history.length > 0) && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#023F80] text-white text-[9px] font-bold flex items-center justify-center">{x.history.length}</span>
                          )}
                        </button>
                        {!isClient && (
                          <>
                            <button
                              onClick={() => openComplete(x)}
                              title="Registra sopralluogo effettuato"
                              data-testid={`sopralluoghi-complete-${x.id}`}
                              className="p-1.5 rounded-md hover:bg-emerald-50"
                            >
                              <CheckCircle2 size={14} className="text-emerald-600" />
                            </button>
                            <button onClick={() => crud.onEdit(x)} title="Modifica" data-testid={`sopralluoghi-edit-${x.id}`} className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Pencil size={14} className="text-gray-600" /></button>
                            <button onClick={() => crud.onDelete(x)} title="Elimina" data-testid={`sopralluoghi-delete-${x.id}`} className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><X size={15} className="text-red-600" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-10 text-center text-sm text-gray-400">Nessun sopralluogo trovato</div>
          )}
        </div>
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(v) => { if (!v) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica sopralluogo' : 'Nuovo sopralluogo'}
        description="Pianifica il prossimo controllo e attiva un alert email automatico."
        fields={sopralluogoFields(companies || [], isClient, isClient ? user?.company : null)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
        submitLabel={crud.editing?.id ? 'Aggiorna' : 'Crea sopralluogo'}
      />

      {/* Complete inspection dialog */}
      <Dialog open={!!completing} onOpenChange={(v) => { if (!v) setCompleting(null); }}>
        <DialogContent className="max-w-md" data-testid="sopralluoghi-complete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><CheckCircle2 size={18} className="text-emerald-600" /> Registra sopralluogo effettuato</DialogTitle>
            <DialogDescription>
              {completing && (<>Per <strong>{companyName(completing.companyId)}</strong> · {(completing.tipi || []).map((t) => (TIPO_BADGE[t]?.label) || t).join(' + ')}</>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700">Data esecuzione</label>
              <Input type="date" value={compForm.date} onChange={(e) => setCompForm({ ...compForm, date: e.target.value })} data-testid="complete-date" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Note del sopralluogo</label>
              <Textarea rows={3} value={compForm.note} onChange={(e) => setCompForm({ ...compForm, note: e.target.value })} data-testid="complete-note" className="mt-1" placeholder="Esito, raccomandazioni, criticità rilevate…" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Prossimo sopralluogo (opzionale)</label>
              <Input type="date" value={compForm.nextDate} onChange={(e) => setCompForm({ ...compForm, nextDate: e.target.value })} data-testid="complete-next" className="mt-1" />
              <p className="text-[11px] text-slate-500 mt-1">Lascia vuoto per non modificare la pianificazione attuale.</p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCompleting(null)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Annulla</button>
            <button
              onClick={submitComplete}
              disabled={submittingComp}
              data-testid="complete-submit"
              className="ro-hide px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1"
            >
              {submittingComp ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Registra
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => { if (!v) setHistoryOf(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="sopralluoghi-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><History size={18} className="text-[#023F80]" /> Storico sopralluoghi</DialogTitle>
            <DialogDescription>
              {historyOf && (<><strong>{companyName(historyOf.companyId)}</strong> · {(historyOf.tipi || []).map((t) => (TIPO_BADGE[t]?.label) || t).join(' + ')}</>)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto py-2">
            {(!historyOf?.history || historyOf.history.length === 0) ? (
              <div className="py-10 text-center text-sm text-slate-400">
                Nessun sopralluogo registrato.
              </div>
            ) : (
              <ul className="space-y-3">
                {[...(historyOf.history || [])].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((h) => (
                  <li key={h.id} className="border border-slate-200 rounded-xl p-3 bg-slate-50/40">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#023F80]">{fmtDate(h.date)}</span>
                        <div className="flex flex-wrap gap-1">
                          {(h.tipi || []).map((t) => {
                            const b = TIPO_BADGE[t] || { bg: '#f1f5f9', color: '#475569', label: t };
                            return <span key={t} className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider" style={{ background: b.bg, color: b.color }}>{b.label}</span>;
                          })}
                        </div>
                      </div>
                      {!isClient && (
                        <button
                          onClick={() => removeHistoryEntry(historyOf.id, h.id)}
                          title="Rimuovi voce"
                          data-testid={`history-delete-${h.id}`}
                          className="p-1 rounded-md hover:bg-red-50"
                        >
                          <Trash2 size={13} className="text-red-600" />
                        </button>
                      )}
                    </div>
                    {h.note && <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">{h.note}</p>}
                    <div className="text-[10px] text-slate-400 mt-2">
                      Registrato da <strong>{h.recordedByUsername || '—'}</strong> il {new Date(h.recordedAt).toLocaleString('it-IT')}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setHistoryOf(null)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Chiudi</button>
            {!isClient && historyOf && (
              <button
                onClick={() => { const r = historyOf; setHistoryOf(null); openComplete(r); }}
                className="ro-hide px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1"
                data-testid="history-add-from-dialog"
              >
                <Plus size={13} /> Aggiungi sopralluogo
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Kpi = ({ label, value, bg, color }) => (
  <div className="rounded-xl p-3" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</div>
    <div className="mt-1 text-2xl font-extrabold" style={{ color }}>{value}</div>
  </div>
);

export default SopralluoghiPage;
